import type { CatalogFormContext } from "@school-sales-ai/shared";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export type CatalogFieldsValues = {
  cycleId: string | undefined;
  productId: string | undefined;
  capturedFields: Record<string, string>;
};

type Props = {
  context: CatalogFormContext;
  values: CatalogFieldsValues;
  onChange: (values: CatalogFieldsValues) => void;
  disabled?: boolean;
  /**
   * Whether to offer the program select. False in the EDIT modal, where the
   * lead's programs of interest are managed by their own section: a lead can
   * hold several, and a single select both misrepresents that and would be a
   * second writer of `capturedFields.programa`. Creation keeps it — there is no
   * lead yet to attach an interest to.
   */
  showProduct?: boolean;
};

const FIELD_TYPE_INPUT_TYPE: Record<string, string> = {
  number: "number",
  date: "date",
  phone: "tel",
  email: "email",
  text: "text",
};

export function CatalogFields({
  context,
  values,
  onChange,
  disabled = false,
  showProduct = true,
}: Props): React.JSX.Element {
  function handleCycleChange(cycleId: string): void {
    onChange({ ...values, cycleId: cycleId || undefined });
  }

  // Deliberately does NOT mirror the name into `capturedFields.programa`. That
  // key was a denormalized copy of the product name from before programs of
  // interest were a table; it is now redundant with the join, and writing it
  // here made this form a second writer of it.
  function handleProductChange(productId: string): void {
    onChange({ ...values, productId: productId || undefined });
  }

  function handleCustomFieldChange(fieldName: string, value: string): void {
    const updatedCapturedFields = value
      ? { ...values.capturedFields, [fieldName]: value }
      : Object.fromEntries(
          Object.entries(values.capturedFields).filter(([k]) => k !== fieldName),
        );
    onChange({ ...values, capturedFields: updatedCapturedFields });
  }

  return (
    <div className="flex flex-col gap-4 border-t border-gray-100 pt-4 mt-2">
      <p className="text-xs font-semibold uppercase tracking-wide text-ink-400">
        Información de interés
      </p>

      {context.cycles.length > 0 && (
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-ink-700">
            Ciclo escolar{" "}
            <span className="text-xs text-ink-400">(opcional)</span>
          </label>
          <Select
            value={values.cycleId ?? ""}
            onValueChange={handleCycleChange}
            disabled={disabled}
          >
            <SelectTrigger>
              <SelectValue placeholder="Sin ciclo asignado" />
            </SelectTrigger>
            <SelectContent>
              {context.cycles.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {showProduct && context.products.length > 0 && (
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-ink-700">
            Programa{" "}
            <span className="text-xs text-ink-400">(opcional)</span>
          </label>
          <Select
            value={values.productId ?? ""}
            onValueChange={handleProductChange}
            disabled={disabled}
          >
            <SelectTrigger>
              <SelectValue placeholder="Sin programa asignado" />
            </SelectTrigger>
            <SelectContent>
              {context.products.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {context.customFields.map((field) =>
        field.fieldType === "select" && field.options ? (
          <div key={field.id} className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-ink-700">
              {field.label}{" "}
              <span className="text-xs text-ink-400">(opcional)</span>
            </label>
            <Select
              value={values.capturedFields[field.name] ?? ""}
              onValueChange={(v) => handleCustomFieldChange(field.name, v)}
              disabled={disabled}
            >
              <SelectTrigger>
                <SelectValue placeholder={`Seleccionar ${field.label.toLowerCase()}`} />
              </SelectTrigger>
              <SelectContent>
                {field.options.map((opt) => (
                  <SelectItem key={opt} value={opt}>
                    {opt}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : (
          <div key={field.id} className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-ink-700">
              {field.label}{" "}
              <span className="text-xs text-ink-400">(opcional)</span>
            </label>
            <Input
              type={FIELD_TYPE_INPUT_TYPE[field.fieldType] ?? "text"}
              value={values.capturedFields[field.name] ?? ""}
              onChange={(e) => handleCustomFieldChange(field.name, e.target.value)}
              placeholder={field.label}
              disabled={disabled}
            />
          </div>
        ),
      )}
    </div>
  );
}
